import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Ban } from "lucide-react";
import { NoAccess } from "../equipment/shared/NoAccess";

const HO_ROLES = ["Director", "Manager", "Back Office"];

export default function LabourCategoryTab() {
  const { user } = useAuth();
  const canManage = HO_ROLES.includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [editing, setEditing] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    client
      .get("/site-ops/masters/labour_category", { params: { include_inactive: includeInactive } })
      .then((res) => setItems(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [includeInactive]);

  const addItem = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return toast.error("Value is required");
    setBusy(true);
    try {
      await client.post("/site-ops/masters/labour_category", { code: newLabel.trim(), label: newLabel.trim() });
      toast.success("Labour Category added");
      setNewLabel("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const rename = async (code) => {
    if (!editLabel.trim()) return toast.error("Value cannot be empty");
    setBusy(true);
    try {
      await client.put(`/site-ops/masters/labour_category/${encodeURIComponent(code)}`, { label: editLabel.trim() });
      toast.success("Labour Category renamed");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (code) => {
    setBusy(true);
    try {
      await client.delete(`/site-ops/masters/labour_category/${encodeURIComponent(code)}`);
      toast.success("Labour Category deactivated");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!canManage) return <NoAccess />;

  return (
    <div className="space-y-4" data-testid="labour-category-tab">
      <form onSubmit={addItem} className="flex gap-2 max-w-md">
        <Input data-testid="labour-category-new-input" placeholder="New Labour Category" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
        <Button type="submit" disabled={busy} data-testid="labour-category-add-button" className="bg-amber-600 hover:bg-amber-700">
          <Plus className="h-4 w-4 mr-1.5" /> Add
        </Button>
      </form>

      <button
        data-testid="labour-category-include-inactive-toggle"
        onClick={() => setIncludeInactive((v) => !v)}
        className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
          includeInactive ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
        }`}
      >
        {includeInactive ? "Showing All" : "Showing Active Only"}
      </button>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="labour-category-table">
          <TableHeader>
            <TableRow>
              <TableHead>Value</TableHead>
              <TableHead>Usage Count</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-slate-400">
                  Loading...
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-slate-400">
                  No categories found.
                </TableCell>
              </TableRow>
            ) : (
              items.map((it) => (
                <TableRow key={it.code} data-testid={`labour-category-row-${it.code}`}>
                  <TableCell>
                    {editing === it.code ? (
                      <Input className="h-8" data-testid={`labour-category-edit-input-${it.code}`} value={editLabel} onChange={(e) => setEditLabel(e.target.value)} />
                    ) : (
                      <span className="font-medium">{it.label}</span>
                    )}
                  </TableCell>
                  <TableCell>{it.usage_count}</TableCell>
                  <TableCell>
                    <Badge className={it.active === false ? "bg-slate-400" : "bg-emerald-600"}>{it.active === false ? "Inactive" : "Active"}</Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    {editing === it.code ? (
                      <>
                        <Button size="sm" disabled={busy} data-testid={`labour-category-save-${it.code}`} onClick={() => rename(it.code)} className="bg-amber-600 hover:bg-amber-700">
                          Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(null)}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`labour-category-edit-${it.code}`}
                          onClick={() => {
                            setEditing(it.code);
                            setEditLabel(it.label);
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {it.active !== false && (
                          <Button size="sm" variant="destructive" disabled={busy} data-testid={`labour-category-deactivate-${it.code}`} onClick={() => deactivate(it.code)}>
                            <Ban className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
